import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/* ─── Types ─────────────────────────────────────────────────────────────────── */
interface HandicapConfig {
  base_average: number;
  handicap_percentage: number;
  calculation_frequency: string;
  lock_handicap_after_weeks: number | null;
  lock_starting_hdcp_for_weeks: number | null;
  a_max: number | null;
  b_max: number | null;
  c_max: number | null;
  d_max: number | null;
  e_max: number | null;
  league_max: number | null;
}

interface BowlerHdcp {
  id: number;
  name: string;
  email: string;
  league_handicap: number;
  average: number | null;      // computed from game scores
  set_average: number | null;  // manually set by bookie
  gender: string | null;
  dob: string | null;
}

interface Props {
  leagueId: number;
  leagueName: string;
  onClose: () => void;
}

const DEFAULT_CONFIG: HandicapConfig = {
  base_average: 200,
  handicap_percentage: 80,
  calculation_frequency: 'per_league',
  lock_handicap_after_weeks: null,
  lock_starting_hdcp_for_weeks: null,
  a_max: null,
  b_max: null,
  c_max: null,
  d_max: null,
  e_max: null,
  league_max: null,
};

/* ─── BookieHandicapsModal ───────────────────────────────────────────────────── */
const BookieHandicapsModal: React.FC<Props> = ({ leagueId, leagueName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingBowlers, setSavingBowlers] = useState(false);
  const [configMsg, setConfigMsg] = useState('');
  const [bowlersMsg, setBowlersMsg] = useState('');

  const [config, setConfig] = useState<HandicapConfig>(DEFAULT_CONFIG);
  const [bowlers, setBowlers] = useState<BowlerHdcp[]>([]);
  const [hdcpEdits, setHdcpEdits] = useState<Record<number, string>>({});
  const [avgEdits, setAvgEdits] = useState<Record<number, string>>({});
  const [genderEdits, setGenderEdits] = useState<Record<number, string>>({});
  const [dobEdits, setDobEdits] = useState<Record<number, string>>({});
  const [dobEditingRow, setDobEditingRow] = useState<number | null>(null);
  const [showConfigSection, setShowConfigSection] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [calcSource, setCalcSource] = useState<'set' | 'computed'>('set');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, bowlersRes] = await Promise.all([
        axios.get(`${API}/bookie-handicaps/leagues/${leagueId}/config`, { headers: authHeader() }),
        axios.get(`${API}/bookie-handicaps/leagues/${leagueId}/bowlers`, { headers: authHeader() }),
      ]);
      if (configRes.data.config) {
        setConfig({ ...DEFAULT_CONFIG, ...configRes.data.config });
        setShowConfigSection(true);
      }
      const bl: BowlerHdcp[] = bowlersRes.data.bowlers || [];
      setBowlers(bl);
      const edits: Record<number, string> = {};
      const aEdits: Record<number, string> = {};
      const gEdits: Record<number, string> = {};
      const dEdits: Record<number, string> = {};
      for (const b of bl) {
        edits[b.id] = String(b.league_handicap);
        aEdits[b.id] = b.set_average != null ? String(b.set_average) : '';
        gEdits[b.id] = b.gender || '';
        dEdits[b.id] = b.dob ? b.dob.split('T')[0] : '';
      }
      setHdcpEdits(edits);
      setAvgEdits(aEdits);
      setGenderEdits(gEdits);
      setDobEdits(dEdits);
    } catch { }
    finally { setLoading(false); }
  }, [leagueId]);

  useEffect(() => { load(); }, [load]);

  const setConfigField = (field: keyof HandicapConfig, value: any) =>
    setConfig(c => ({ ...c, [field]: value === '' ? null : value }));

  const saveConfig = async () => {
    setSavingConfig(true); setConfigMsg('');
    try {
      await axios.put(`${API}/bookie-handicaps/leagues/${leagueId}/config`, config, { headers: authHeader() });
      setConfigMsg('Configuration saved!');
      setTimeout(() => setConfigMsg(''), 3000);
    } catch {
      setConfigMsg('Failed to save configuration.');
    } finally { setSavingConfig(false); }
  };

  const saveBowlers = async () => {
    setSavingBowlers(true); setBowlersMsg('');
    try {
      const updates = bowlers.map(b => ({
        bowlerId: b.id,
        handicap: parseInt(hdcpEdits[b.id] || '0', 10) || 0,
        set_average: avgEdits[b.id] !== '' && avgEdits[b.id] != null ? Number(avgEdits[b.id]) : null,
        gender: genderEdits[b.id] || null,
        dob: dobEdits[b.id] || null,
      }));
      await axios.put(`${API}/bookie-handicaps/leagues/${leagueId}/bowlers/handicaps`,
        { updates }, { headers: authHeader() });
      setBowlersMsg('Handicaps & info saved!');
      setTimeout(() => setBowlersMsg(''), 3000);
    } catch {
      setBowlersMsg('Failed to save handicaps.');
    } finally { setSavingBowlers(false); }
  };

  // ── Name formatter: "First Last" → "Last, First"
  const fmt = (name: string) => { const i = name.indexOf(' '); return i === -1 ? name : `${name.slice(i + 1)}, ${name.slice(0, i)}`; };

  // ── Filtered bowler list
  const filteredBowlers = useMemo(() => {
    if (!searchQuery.trim()) return bowlers;
    const q = searchQuery.toLowerCase();
    const normalized = (q.includes(' ') && !q.includes(',')) ? q.replace(' ', ', ') : q;
    return bowlers.filter(b =>
      fmt(b.name).toLowerCase().includes(normalized) ||
      b.name.toLowerCase().includes(q) ||
      (b.email && b.email.toLowerCase().includes(q))
    );
  }, [searchQuery, bowlers]);

  // ── Pre-fill handicaps using current config + chosen avg source
  const calculateHandicaps = () => {
    const newEdits: Record<number, string> = {};
    for (const b of bowlers) {
      const avg = calcSource === 'computed' && b.average != null
        ? Number(b.average)
        : avgEdits[b.id] !== '' && avgEdits[b.id] != null
          ? Number(avgEdits[b.id])
          : null;
      if (avg == null) continue;
      const raw = (config.base_average - avg) * (config.handicap_percentage / 100);
      let hdcp = Math.max(0, Math.floor(raw));
      if (config.league_max != null) hdcp = Math.min(hdcp, Number(config.league_max));
      newEdits[b.id] = String(hdcp);
    }
    setHdcpEdits(prev => ({ ...prev, ...newEdits }));
    setBowlersMsg(`Handicaps pre-filled — review and click Save to confirm.`);
    setTimeout(() => setBowlersMsg(''), 5000);
  };

  const inputCls = 'w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-purple-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';
  const labelCls = 'block text-xs text-gray-400 mb-1';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
          <div>
            <h2 className="font-bold text-white text-lg">Set Handicaps, Gender and Age</h2>
            <p className="text-xs text-gray-400">{leagueName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-7 h-7 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* ── Handicap Config Section (collapsible) ── */}
              <div className="bg-gray-700/30 border border-gray-600/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowConfigSection(v => !v)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-700/40 transition-colors text-left">
                  <div>
                    <span className="font-semibold text-white text-sm">⚙️ Handicap Configuration</span>
                    <span className="text-xs text-gray-400 ml-2">(optional — defines the formula used)</span>
                  </div>
                  <svg className={`w-4 h-4 text-gray-400 transition-transform ${showConfigSection ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showConfigSection && (
                  <div className="px-5 pb-5 space-y-5">
                    {/* Row 1 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Base Average</label>
                        <input type="number" min={100} max={300} step={5}
                          value={config.base_average ?? 200}
                          onChange={e => setConfigField('base_average', parseInt(e.target.value))}
                          className={inputCls} />
                        <p className="text-[10px] text-gray-500 mt-1">Score used as the benchmark (e.g. 200, 210)</p>
                      </div>
                      <div>
                        <label className={labelCls}>Handicap Percentage (%)</label>
                        <input type="number" min={0} max={100} step={5}
                          value={config.handicap_percentage ?? 80}
                          onChange={e => setConfigField('handicap_percentage', parseFloat(e.target.value))}
                          className={inputCls} />
                        <p className="text-[10px] text-gray-500 mt-1">% of difference applied (e.g. 80%)</p>
                      </div>
                    </div>

                    {/* Row 2 */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Calculation Frequency</label>
                        <select
                          value={config.calculation_frequency ?? 'per_league'}
                          onChange={e => setConfigField('calculation_frequency', e.target.value)}
                          className={inputCls}>
                          <option value="per_league">Per League</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>League Max Handicap</label>
                        <input type="number" min={0} max={500}
                          value={config.league_max ?? ''}
                          onChange={e => setConfigField('league_max', e.target.value === '' ? null : parseInt(e.target.value))}
                          placeholder="No limit"
                          className={inputCls} />
                      </div>
                    </div>

                    {/* Division Caps */}
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Division Handicap Caps <span className="text-gray-600">(leave blank for no cap)</span></p>
                      <div className="grid grid-cols-5 gap-3">
                        {(['a', 'b', 'c', 'd', 'e'] as const).map(div => (
                          <div key={div}>
                            <label className="block text-[10px] text-gray-500 mb-1 text-center">DIV {div.toUpperCase()}</label>
                            <input type="number" min={0} max={500}
                              value={config[`${div}_max` as keyof HandicapConfig] ?? ''}
                              onChange={e => setConfigField(`${div}_max` as keyof HandicapConfig, e.target.value === '' ? null : parseInt(e.target.value))}
                              placeholder="—"
                              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-xs text-center focus:outline-none focus:border-purple-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Lock options */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Lock Handicap After Weeks</label>
                        <input type="number" min={1} max={52}
                          value={config.lock_handicap_after_weeks ?? ''}
                          onChange={e => setConfigField('lock_handicap_after_weeks', e.target.value === '' ? null : parseInt(e.target.value))}
                          placeholder="Never lock"
                          className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Lock Starting HDCP for Weeks</label>
                        <input type="number" min={1} max={52}
                          value={config.lock_starting_hdcp_for_weeks ?? ''}
                          onChange={e => setConfigField('lock_starting_hdcp_for_weeks', e.target.value === '' ? null : parseInt(e.target.value))}
                          placeholder="No lock"
                          className={inputCls} />
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button onClick={saveConfig} disabled={savingConfig}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2">
                        {savingConfig && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                        Save Configuration
                      </button>
                      {configMsg && (
                        <span className={`text-xs font-medium ${configMsg.includes('Failed') ? 'text-red-400' : 'text-green-400'}`}>
                          {configMsg}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Bowler Handicaps ── */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-bold text-white">Bowler Handicaps</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Set the handicap for each bowler. These values are added to every game score when handicap mode is active.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {bowlersMsg && (
                      <span className={`text-xs font-medium ${bowlersMsg.includes('Failed') ? 'text-red-400' : bowlersMsg.includes('pre-filled') ? 'text-amber-400' : 'text-green-400'}`}>
                        {bowlersMsg}
                      </span>
                    )}
                    <button onClick={saveBowlers} disabled={savingBowlers || bowlers.length === 0}
                      className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white text-sm rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2">
                      {savingBowlers && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                      Save Handicaps & Info
                    </button>
                  </div>
                </div>

                {/* ── Calculate bar ── */}
                {bowlers.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 p-3 bg-gray-700/30 border border-gray-600/40 rounded-lg">
                    <span className="text-xs text-gray-400 font-medium">Calculate using:</span>
                    <button
                      onClick={() => setCalcSource('set')}
                      className={`px-3 py-1 text-xs rounded-md font-semibold transition-colors ${calcSource === 'set' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}>
                      Set Avg
                    </button>
                    <button
                      onClick={() => setCalcSource('computed')}
                      className={`px-3 py-1 text-xs rounded-md font-semibold transition-colors ${calcSource === 'computed' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}>
                      Game Avg
                    </button>
                    <button onClick={calculateHandicaps} disabled={bowlers.length === 0}
                      className="ml-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs rounded-lg font-bold disabled:opacity-40 flex items-center gap-1">
                      ⚡ Calculate Handicaps
                    </button>
                    <span className="text-[10px] text-gray-600 ml-1">Pre-fills all fields — review then Save</span>
                  </div>
                )}

                {/* ── Search bar ── */}
                {bowlers.length > 0 && (
                  <div className="relative mb-3">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search by name or email…"
                      className="w-full pl-9 pr-8 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-400"
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}

                {bowlers.length === 0 ? (
                  <div className="text-center text-gray-500 py-12 text-sm bg-gray-700/20 rounded-xl border border-gray-700/50">
                    <div className="text-3xl mb-3">🎳</div>
                    <p>No bowlers registered in this league yet.</p>
                    <p className="text-xs mt-1 text-gray-600">Add bowlers to side events first.</p>
                  </div>
                ) : (
                  <div className="bg-gray-700/20 border border-gray-700/50 rounded-xl overflow-hidden">
                    {/* Table header */}
                    <div className="grid grid-cols-12 items-center px-4 py-2.5 bg-gray-700/60 border-b border-gray-700/60">
                      <div className="col-span-1 text-[10px] text-gray-500 font-medium uppercase">#</div>
                      <div className="col-span-3 text-[10px] text-gray-500 font-medium uppercase">Bowler</div>
                      <div className="col-span-2 text-center">
                        <div className="text-[10px] text-gray-400 font-medium uppercase">Set Avg</div>
                        <div className="text-[9px] text-blue-500/70">Game Avg</div>
                      </div>
                      <div className="col-span-2 text-[10px] text-gray-500 font-medium uppercase text-center">Gender</div>
                      <div className="col-span-2 text-[10px] text-gray-500 font-medium uppercase text-center">Age</div>
                      <div className="col-span-2 text-[10px] text-gray-500 font-medium uppercase text-center">Handicap</div>
                    </div>
                    {/* Rows */}
                    <div className="divide-y divide-gray-700/40">
                      {filteredBowlers.map((b, i) => {
                        const dobVal = dobEdits[b.id] || b.dob;
                        const age = dobVal ? Math.floor((Date.now() - new Date(dobVal).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : null;
                        return (
                          <div key={b.id} className="grid grid-cols-12 items-center px-4 py-2.5 hover:bg-gray-700/20 transition-colors">
                            <div className="col-span-1 text-xs text-gray-600">{i + 1}</div>
                            <div className="col-span-3">
                              <div className="text-sm text-white font-medium truncate">{b.name}</div>
                              <div className="text-[11px] text-gray-500 truncate">{b.email}</div>
                            </div>
                            {/* Set Avg (editable) + Game Avg (computed, read-only) */}
                            <div className="col-span-2 flex flex-col items-center gap-0.5">
                              <input
                                type="number"
                                min={0} max={300} step={0.5}
                                value={avgEdits[b.id] ?? ''}
                                onChange={e => setAvgEdits(prev => ({ ...prev, [b.id]: e.target.value }))}
                                onFocus={e => e.target.select()}
                                placeholder="—"
                                title="Set average (manual starting average)"
                                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs text-center focus:outline-none focus:border-purple-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              />
                              {b.average != null ? (
                                <span className="text-[10px] font-mono text-blue-400" title="Computed from game scores">
                                  {Number(b.average).toFixed(1)}
                                </span>
                              ) : (
                                <span className="text-[10px] text-gray-700">—</span>
                              )}
                            </div>
                            <div className="col-span-2 flex justify-center">
                              <select
                                value={genderEdits[b.id] ?? ''}
                                onChange={e => setGenderEdits(prev => ({ ...prev, [b.id]: e.target.value }))}
                                className="w-20 px-1.5 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-xs focus:outline-none focus:border-purple-400">
                                <option value="">—</option>
                                <option value="M">Male</option>
                                <option value="F">Female</option>
                              </select>
                            </div>
                            <div className="col-span-2 flex justify-center items-center">
                              {dobEditingRow === b.id ? (
                                <input
                                  type="date"
                                  autoFocus
                                  value={dobEdits[b.id] ?? ''}
                                  max={new Date().toISOString().split('T')[0]}
                                  onChange={e => {
                                    setDobEdits(prev => ({ ...prev, [b.id]: e.target.value }));
                                  }}
                                  onBlur={() => setDobEditingRow(null)}
                                  className="w-28 px-2 py-1 bg-gray-700 border border-purple-500 rounded text-white text-xs focus:outline-none [color-scheme:dark]"
                                />
                              ) : (
                                <button
                                  onClick={() => setDobEditingRow(b.id)}
                                  title="Click to set date of birth"
                                  className="text-sm font-mono text-gray-300 hover:text-white underline decoration-dotted decoration-gray-500 cursor-pointer transition-colors">
                                  {age != null ? age : <span className="text-gray-600 text-xs">+ DOB</span>}
                                </button>
                              )}
                            </div>
                            <div className="col-span-2 flex justify-center">
                              <input
                                type="number"
                                min={0}
                                max={300}
                                value={hdcpEdits[b.id] ?? '0'}
                                onChange={e => setHdcpEdits(prev => ({ ...prev, [b.id]: e.target.value }))}
                                onFocus={e => e.target.select()}
                                className="w-16 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm text-center focus:outline-none focus:border-purple-400 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              />
                            </div>
                          </div>
                        );
                      })}
                      {filteredBowlers.length === 0 && searchQuery && (
                        <div className="text-center text-gray-500 py-8 text-sm">
                          No bowlers found matching &ldquo;{searchQuery}&rdquo;
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3 flex-shrink-0">
          <button onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-sm rounded-lg text-white">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookieHandicapsModal;
